import React, { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx, tokens } from '../../theme/tokens';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';

type Direction = 'forward' | 'reverse';
type DataType = 'REAL' | 'INT' | 'WORD';

type Preset = {
  label: string;
  x1: string;
  x2: string;
  y1: string;
  y2: string;
  input: string;
  dataType: DataType;
};

const presets: Preset[] = [
  { label: '0-10V -> 0-100%', x1: '0', x2: '10', y1: '0', y2: '100', input: '5', dataType: 'REAL' },
  { label: '4-20mA -> 0-100%', x1: '4', x2: '20', y1: '0', y2: '100', input: '12', dataType: 'REAL' },
  { label: '0-100% -> 0-65535', x1: '0', x2: '100', y1: '0', y2: '65535', input: '50', dataType: 'WORD' },
  { label: '0-65535 -> 0-100%', x1: '0', x2: '65535', y1: '0', y2: '100', input: '32768', dataType: 'INT' },
  { label: '0-100% -> 0-65535', x1: '0', x2: '100', y1: '0', y2: '65535', input: '50', dataType: 'WORD' }
];

const normalizeNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value: number, digits = 3): string =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : Math.min(3, digits),
    maximumFractionDigits: digits
  }).format(value);

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const formatFormulaValue = (value: number): string =>
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(value);

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewSkalierer } = useAuth();
  const [direction, setDirection] = useState<Direction>('forward');
  const [dataType, setDataType] = useState<DataType>('REAL');
  const [x1, setX1] = useState('0');
  const [x2, setX2] = useState('10');
  const [y1, setY1] = useState('0');
  const [y2, setY2] = useState('100');
  const [inputValue, setInputValue] = useState('5');
  const [useClamp, setUseClamp] = useState(true);

  const { output, error, rangeLabel, steps } = useMemo(() => {
    const x1Num = normalizeNumber(x1);
    const x2Num = normalizeNumber(x2);
    const y1Num = normalizeNumber(y1);
    const y2Num = normalizeNumber(y2);
    const inputNum = normalizeNumber(inputValue);

    if (x1Num === null || x2Num === null || y1Num === null || y2Num === null || inputNum === null) {
      return { output: null as number | null, error: null as string | null, rangeLabel: '', steps: [] as string[] };
    }

    const xMin = Math.min(x1Num, x2Num);
    const xMax = Math.max(x1Num, x2Num);
    const yMin = Math.min(y1Num, y2Num);
    const yMax = Math.max(y1Num, y2Num);

    if (direction === 'forward') {
      const denominator = x2Num - x1Num;
      if (Math.abs(denominator) < 1e-9) {
        return { output: null, error: 'x1 und x2 duerfen nicht gleich sein.', rangeLabel: '', steps: [] as string[] };
      }
      let value = y1Num + ((inputNum - x1Num) * (y2Num - y1Num)) / denominator;
      if (useClamp) value = clamp(value, yMin, yMax);
      const ratio = (inputNum - x1Num) / denominator;
      const stepLines = [
        `1) Bereich X: [${formatFormulaValue(x1Num)} .. ${formatFormulaValue(x2Num)}], Bereich Y: [${formatFormulaValue(y1Num)} .. ${formatFormulaValue(y2Num)}]`,
        `2) Normierter Anteil t = (X - x1) / (x2 - x1) = (${formatFormulaValue(inputNum)} - ${formatFormulaValue(x1Num)}) / (${formatFormulaValue(x2Num)} - ${formatFormulaValue(x1Num)}) = ${formatFormulaValue(ratio)}`,
        `3) Y = y1 + t * (y2 - y1) = ${formatFormulaValue(y1Num)} + ${formatFormulaValue(ratio)} * (${formatFormulaValue(y2Num)} - ${formatFormulaValue(y1Num)}) = ${formatFormulaValue(value)}`
      ];
      if (useClamp) {
        stepLines.push(`4) Begrenzung aktiv: Y wird auf [${formatFormulaValue(yMin)} .. ${formatFormulaValue(yMax)}] begrenzt.`);
      }
      return {
        output: value,
        error: null,
        rangeLabel: `Y Bereich: ${formatNumber(yMin)} bis ${formatNumber(yMax)}`,
        steps: stepLines
      };
    }

    const denominator = y2Num - y1Num;
    if (Math.abs(denominator) < 1e-9) {
      return { output: null, error: 'y1 und y2 duerfen nicht gleich sein.', rangeLabel: '', steps: [] as string[] };
    }
    let value = x1Num + ((inputNum - y1Num) * (x2Num - x1Num)) / denominator;
    if (useClamp) value = clamp(value, xMin, xMax);
    const ratio = (inputNum - y1Num) / denominator;
    const stepLines = [
      `1) Bereich Y: [${formatFormulaValue(y1Num)} .. ${formatFormulaValue(y2Num)}], Bereich X: [${formatFormulaValue(x1Num)} .. ${formatFormulaValue(x2Num)}]`,
      `2) Normierter Anteil t = (Y - y1) / (y2 - y1) = (${formatFormulaValue(inputNum)} - ${formatFormulaValue(y1Num)}) / (${formatFormulaValue(y2Num)} - ${formatFormulaValue(y1Num)}) = ${formatFormulaValue(ratio)}`,
      `3) X = x1 + t * (x2 - x1) = ${formatFormulaValue(x1Num)} + ${formatFormulaValue(ratio)} * (${formatFormulaValue(x2Num)} - ${formatFormulaValue(x1Num)}) = ${formatFormulaValue(value)}`
    ];
    if (useClamp) {
      stepLines.push(`4) Begrenzung aktiv: X wird auf [${formatFormulaValue(xMin)} .. ${formatFormulaValue(xMax)}] begrenzt.`);
    }
    return {
      output: value,
      error: null,
      rangeLabel: `X Bereich: ${formatNumber(xMin)} bis ${formatNumber(xMax)}`,
      steps: stepLines
    };
  }, [x1, x2, y1, y2, inputValue, direction, useClamp]);

  const formattedOutput = useMemo(() => {
    if (output === null) return '-';
    let value = output;
    if (dataType === 'INT') value = Math.round(value);
    if (dataType === 'WORD') value = clamp(Math.round(value), 0, 65535);
    return dataType === 'REAL' ? formatNumber(value) : `${Math.round(value)}`;
  }, [output, dataType]);

  const applyPreset = (preset: Preset) => {
    setX1(preset.x1);
    setX2(preset.x2);
    setY1(preset.y1);
    setY2(preset.y2);
    setInputValue(preset.input);
    setDataType(preset.dataType);
  };

  if (!canViewSkalierer) {
    return (
      <UnitPageShell title="Skalierer">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Keine Berechtigung fuer diese Seite.</Alert>
        </Box>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell title="M99 U8 - Skalierer">
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(0, 0.8fr)' } }}>
        <Card sx={getCardSx(darkMode)}>
          <CardContent>
            <Stack spacing={2.5}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h5" fontWeight={700}>Skalierer</Typography>
                  <Tooltip title="Lineare Interpolation zwischen zwei Achspunkten. Vorwaerts und rueckwaerts nutzbar.">
                    <IconButton size="small" aria-label="Info Skalierer">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Definiert eine lineare Kennlinie ueber zwei Punkte und berechnet Werte in beide Richtungen.
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="scale-direction">Richtung</InputLabel>
                  <Select
                    labelId="scale-direction"
                    label="Richtung"
                    value={direction}
                    onChange={(e) => setDirection(e.target.value as Direction)}
                  >
                    <MenuItem value="forward">X → Y</MenuItem>
                    <MenuItem value="reverse">Y → X</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="scale-type">Datentyp</InputLabel>
                  <Select
                    labelId="scale-type"
                    label="Datentyp"
                    value={dataType}
                    onChange={(e) => setDataType(e.target.value as DataType)}
                  >
                    <MenuItem value="REAL">REAL</MenuItem>
                    <MenuItem value="INT">INT</MenuItem>
                    <MenuItem value="WORD">WORD (0-65535)</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="x1"
                  helperText="Startpunkt X"
                  value={x1}
                  onChange={(e) => setX1(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="x2"
                  helperText="Endpunkt X"
                  value={x2}
                  onChange={(e) => setX2(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="y1"
                  helperText="Startpunkt Y"
                  value={y1}
                  onChange={(e) => setY1(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="y2"
                  helperText="Endpunkt Y"
                  value={y2}
                  onChange={(e) => setY2(e.target.value)}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField
                  label={direction === 'forward' ? 'Eingang (X)' : 'Eingang (Y)'}
                  helperText={direction === 'forward' ? 'Messwert auf X-Achse' : 'Soll-/Istwert auf Y-Achse'}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  fullWidth
                />
                <FormControlLabel
                  control={<Switch checked={useClamp} onChange={(e) => setUseClamp(e.target.checked)} />}
                  label="Min/Max begrenzen"
                />
              </Stack>

              {error && <Alert severity="error">{error}</Alert>}

              <Box
                sx={{
                  borderRadius: 2,
                  p: 2,
                  background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                  border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ergebnis
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color={tokens.colors.primary}>
                    {formattedOutput}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {rangeLabel}
                  </Typography>
                </Stack>
              </Box>

              {steps.length > 0 && (
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                    border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="subtitle2" color="text.secondary">
                        Rechenweg
                      </Typography>
                      <Tooltip title="Zeigt die eingesetzten Werte und jeden Rechenschritt in Reihenfolge.">
                        <IconButton size="small" aria-label="Info Rechenweg">
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {steps.map((line) => (
                      <Typography key={line} variant="body2" color="text.secondary">
                        {line}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Stack spacing={2.5}>
          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Vorlagen
              </Typography>
              <Stack spacing={1}>
                {presets.map((preset) => (
                  <Button key={preset.label} variant="outlined" onClick={() => applyPreset(preset)}>
                    {preset.label}
                  </Button>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Formel
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {direction === 'forward'
                  ? 'Y = y1 + (X - x1) * (y2 - y1) / (x2 - x1)'
                  : 'X = x1 + (Y - y1) * (x2 - x1) / (y2 - y1)'}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2" color="text.secondary">
                Datentyp-Ausgabe: REAL = Fließkomma, INT = ganzzahlig gerundet, WORD = ganzzahlig und auf 0..65535 begrenzt.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Anwendung
              </Typography>
              <Stack spacing={0.75}>
                <Typography variant="body2" color="text.secondary">
                  1) Richtung waehlen (X → Y oder Y → X).
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  2) Punkte x1/x2/y1/y2 passend zur Kennlinie eintragen.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  3) Eingangswert eingeben und bei Bedarf Begrenzung aktivieren.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  4) Ergebnis plus Rechenweg pruefen und Datentyp fuer SPS-Ausgabe setzen.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </UnitPageShell>
  );
};

export default Page;
